import { confirmar } from '@/components/ui/confirm-dialog';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Loader2, Save } from 'lucide-react';
import { useToast } from '@/lib/hooks/use-toast';
import {
  freightServicesService,
  type CreateFreightServiceDto,
} from '@/features/import-tracking/services/freight-services-service';
import type { FreightService } from '@/features/import-tracking/types/import-tracking.types';

// Etiquetas legibles para pricingType
const PRICING_TYPE_LABEL: Record<string, string> = {
  FLAT: 'Tarifa fija',
  WEIGHT: 'Por kg',
  VOLUME: 'Por CBM',
};

function formatPrice(service: FreightService): string {
  if (service.referencePrice == null || service.referencePrice === '') return '—';
  const n = Number(service.referencePrice);
  if (Number.isNaN(n)) return '—';
  const currency = service.priceCurrency || '';
  const formatted = n.toLocaleString('es-BO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
  return currency ? `${currency} ${formatted}` : formatted;
}

export function FreightServiceTable() {
  const { toast } = useToast();
  const [services, setServices] = useState<FreightService[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [editingService, setEditingService] = useState<FreightService | null>(null);

  const fetchServices = async () => {
    setIsLoading(true);
    try {
      const data = await freightServicesService.list();
      setServices(data);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const handleDelete = async (service: FreightService) => {
    if (
      !(await confirmar({
        title: `¿Desactivar el servicio "${service.name}"?`,
        variant: 'destructive',
        confirmText: 'Sí, desactivar',
      }))
    )
      return;
    try {
      await freightServicesService.remove(service.id);
      toast({ title: 'Servicio desactivado', variant: 'success' });
      fetchServices();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          onClick={() => {
            setEditingService(null);
            setShowFormDialog(true);
          }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Servicio
        </Button>
      </div>

      {services.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No hay servicios de flete configurados. Crea uno para empezar.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Código</TableHead>
              <TableHead>Nombre</TableHead>
              <TableHead>Categoría</TableHead>
              <TableHead className="text-right">Precio Referencial</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead className="text-center">Estado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {services.map((service) => (
              <TableRow key={service.id}>
                <TableCell className="font-mono text-xs">{service.code}</TableCell>
                <TableCell className="font-medium">
                  {service.name}
                  {service.description && (
                    <p className="text-xs text-muted-foreground truncate max-w-[260px]">
                      {service.description}
                    </p>
                  )}
                </TableCell>
                <TableCell>
                  {service.category ? (
                    <Badge variant="outline">{service.category}</Badge>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right font-mono">{formatPrice(service)}</TableCell>
                <TableCell>
                  {service.pricingType ? (
                    <Badge variant="secondary">
                      {PRICING_TYPE_LABEL[service.pricingType] ?? service.pricingType}
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant={service.isActive ? 'default' : 'secondary'}>
                    {service.isActive ? 'Activo' : 'Inactivo'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setEditingService(service);
                        setShowFormDialog(true);
                      }}
                      aria-label={`Editar ${service.name}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(service)}
                      aria-label={`Eliminar ${service.name}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <FreightServiceFormDialog
        open={showFormDialog}
        onOpenChange={setShowFormDialog}
        service={editingService}
        onSaved={fetchServices}
      />
    </div>
  );
}

// ========================================
// Service Form Dialog
// ========================================

function FreightServiceFormDialog({
  open,
  onOpenChange,
  service,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  service: FreightService | null;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [sapCode, setSapCode] = useState('');
  const [referencePrice, setReferencePrice] = useState<string>('');
  const [priceCurrency, setPriceCurrency] = useState<string>('USD');
  const [pricingType, setPricingType] = useState<'FLAT' | 'WEIGHT' | 'VOLUME' | ''>('FLAT');
  const [isActive, setIsActive] = useState(true);
  const [codeManuallyEdited, setCodeManuallyEdited] = useState(false);

  useEffect(() => {
    if (service) {
      setCode(service.code);
      setName(service.name);
      setCategory(service.category || '');
      setDescription(service.description || '');
      setSapCode(service.sapCode || '');
      setReferencePrice(
        service.referencePrice != null && service.referencePrice !== ''
          ? String(service.referencePrice)
          : ''
      );
      setPriceCurrency(service.priceCurrency || 'USD');
      setPricingType((service.pricingType as any) || 'FLAT');
      setIsActive(service.isActive);
      setCodeManuallyEdited(true);
    } else {
      setCode('');
      setName('');
      setCategory('');
      setDescription('');
      setSapCode('');
      setReferencePrice('');
      setPriceCurrency('USD');
      setPricingType('FLAT');
      setIsActive(true);
      setCodeManuallyEdited(false);
    }
  }, [service, open]);

  // Auto-generar código desde el nombre (slugify a UPPER_SNAKE_CASE)
  // mientras el usuario no haya editado el código a mano.
  const slugifyToCode = (input: string): string =>
    input
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '') // quitar acentos
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 60);

  const handleNameChange = (value: string) => {
    setName(value);
    if (!codeManuallyEdited) {
      setCode(slugifyToCode(value));
    }
  };

  const handleCodeChange = (value: string) => {
    setCode(value);
    setCodeManuallyEdited(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const refPriceNum = referencePrice.trim() === '' ? null : Number(referencePrice);
      if (refPriceNum != null && (Number.isNaN(refPriceNum) || refPriceNum < 0)) {
        toast({
          title: 'Precio inválido',
          description: 'El precio referencial debe ser un número >= 0',
          variant: 'destructive',
        });
        setIsSaving(false);
        return;
      }
      const data: CreateFreightServiceDto = {
        code: code.trim(),
        name: name.trim(),
        category: category.trim() || null,
        description: description.trim() || null,
        sapCode: sapCode.trim() || null,
        referencePrice: refPriceNum,
        priceCurrency: refPriceNum != null ? priceCurrency.trim().toUpperCase() || null : null,
        pricingType: refPriceNum != null && pricingType ? pricingType : null,
        isActive,
      };
      if (service) {
        await freightServicesService.update(service.id, data);
        toast({ title: 'Servicio actualizado', variant: 'success' });
      } else {
        await freightServicesService.create(data);
        toast({ title: 'Servicio creado', variant: 'success' });
      }
      onOpenChange(false);
      onSaved();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{service ? 'Editar Servicio' : 'Nuevo Servicio de Flete'}</DialogTitle>
          <DialogDescription>
            Define un servicio de flete (manipuleo, almacenaje, despacho aduanero, etc.) con su
            precio referencial.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>
                Código
                {!service && !codeManuallyEdited && (
                  <span className="ml-1 text-[10px] text-muted-foreground">(auto)</span>
                )}
              </Label>
              <Input
                value={code}
                onChange={(e) => handleCodeChange(e.target.value)}
                placeholder="HANDLING_ORIG"
                required
              />
            </div>
            <div>
              <Label>Categoría</Label>
              <Input
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="Manipuleo, Aduana, Almacenaje..."
              />
            </div>
          </div>
          <div>
            <Label>Nombre</Label>
            <Input
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Manipuleo en origen"
              required
            />
          </div>
          <div>
            <Label>Descripción</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descripción del servicio (opcional)"
              rows={2}
            />
          </div>

          {/* Precio referencial */}
          <div className="rounded-md border border-blue-200 bg-blue-50/30 p-3 space-y-3">
            <p className="text-sm font-medium">Precio referencial del catálogo</p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Precio</Label>
                <Input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={referencePrice}
                  onChange={(e) => setReferencePrice(e.target.value)}
                  placeholder="Ej: 150"
                />
              </div>
              <div>
                <Label>Moneda</Label>
                <Input
                  value={priceCurrency}
                  onChange={(e) => setPriceCurrency(e.target.value.toUpperCase())}
                  placeholder="USD"
                  maxLength={10}
                />
              </div>
              <div>
                <Label>Tipo</Label>
                <Select
                  value={pricingType || 'FLAT'}
                  onValueChange={(v) => setPricingType(v as any)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FLAT">Tarifa fija</SelectItem>
                    <SelectItem value="WEIGHT">Por kg</SelectItem>
                    <SelectItem value="VOLUME">Por CBM</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Este precio se aplica como referencia al cotizar. Dejá vacío si el servicio no tiene
              precio fijo.
            </p>
          </div>

          {/* SAP code */}
          <div>
            <Label>Código SAP (opcional)</Label>
            <Input
              value={sapCode}
              onChange={(e) => setSapCode(e.target.value)}
              placeholder="Código equivalente en SAP"
              maxLength={64}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="freight-service-active"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="w-4 h-4"
            />
            <Label htmlFor="freight-service-active" className="cursor-pointer">
              Activo
            </Label>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Save className="w-4 h-4 mr-2" />
              {service ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
